import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaFilePdf } from 'react-icons/fa';
import CatalogModal from './CatalogModal';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (path: string) => {
    if (path.startsWith('#')) {
      // Tenta fazer scroll na página atual primeiro
      const element = document.querySelector(path);
      if (element) {
        // Se o elemento existe na página atual, faz scroll
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (location.pathname !== '/') {
        // Se não existe e não está na home, vai para home
        navigate('/');
        // Aguarda a navegação e depois faz scroll
        setTimeout(() => {
          const homeElement = document.querySelector(path);
          if (homeElement) {
            homeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'Início', path: '/', isAnchor: false },
    { name: 'Jardins Verticais', path: '/jardins-verticais', isAnchor: false },
    { name: 'Paisagismo e Manutenção', path: '/paisagismo', isAnchor: false },
    { name: 'Contato', path: '#contato', isAnchor: true },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/images/logo-transparent.png"
              alt="Arte Verde Jardins"
              className="h-12 md:h-16 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              item.isAnchor ? (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.path)}
                  className={`font-medium transition-colors duration-300 relative group ${
                    isScrolled ? 'text-floresta hover:text-natureza' : 'text-white hover:text-argila'
                  }`}
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-natureza transition-all duration-300 group-hover:w-full" />
                </button>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`font-medium transition-colors duration-300 relative group ${
                    isScrolled ? 'text-floresta hover:text-natureza' : 'text-white hover:text-argila'
                  }`}
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-natureza transition-all duration-300 group-hover:w-full" />
                </Link>
              )
            ))}

            <button
              onClick={() => setIsCatalogModalOpen(true)}
              className="btn-primary flex items-center gap-2"
            >
              <FaFilePdf size={20} />
              Catálogo
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-dark' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white shadow-xl"
          >
            <div className="container-custom py-6 space-y-4">
              {navItems.map((item) => (
                item.isAnchor ? (
                  <button
                    key={item.name}
                    onClick={() => handleNavClick(item.path)}
                    className="block py-3 text-dark hover:text-primary-500 font-medium transition-colors border-b border-gray-100 last:border-0 w-full text-left"
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-3 text-dark hover:text-primary-500 font-medium transition-colors border-b border-gray-100 last:border-0"
                  >
                    {item.name}
                  </Link>
                )
              ))}

              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsCatalogModalOpen(true);
                }}
                className="btn-primary flex items-center justify-center gap-2 w-full"
              >
                <FaFilePdf size={20} />
                Catálogo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Catalog Modal */}
      <CatalogModal
        isOpen={isCatalogModalOpen}
        onClose={() => setIsCatalogModalOpen(false)}
      />
    </motion.header>
  );
};

export default Header;
